import React, { useEffect, useState } from 'react';
import { toast } from '../../../../../../../ui/toast';
import { Dialog, Drawer } from '../../../../../../../ui';
import { TrashIcon } from '../../../../../../../ui/icons/icons';
import { deleteAssessmentTemplate, getAssessmentTemplateList } from '../../../../../../../utils/api_service/assessment_api';
import { AssessmentTemplateRequest, SimplifiedTemplate } from '../../../../../../../utils/types/assessment_types';
import { DIALOG_TYPE } from '../../../../../Constants/DialogTypes';

interface TemplateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: SimplifiedTemplate) => void;
}

export const TemplateDrawer: React.FC<TemplateDrawerProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => {

  const [templates, setTemplates] = useState<SimplifiedTemplate[]>([]);
  const [templateSearch, setTemplateSearch] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [deleteTemplate, setDeleteTemplate] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 8;

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const getAssessmentTemplates = async () => {
    const requestBody: AssessmentTemplateRequest = {
      jobTitle: '',
      templateName: templateSearch,
    };

    try {
      const response = await getAssessmentTemplateList(requestBody, pageNumber, itemsPerPage);
      if (response.ok) {
        const data = await response.json();
        const formattedTemplates: SimplifiedTemplate[] = data.results.map(
          (item: any) => ({
            templateId: item.id,
            templateTitle: item.templateTitle,
            jobTitle: item.jobTitle?.jobTitle ?? 'N/A',
            createdAt: item.createdAt,
            questionCount: item.questionAnswer?.length ?? 0,
          }),
        );

        setPageNumber(data.page);
        setTotalCount(data.total_count);

        setTemplates(formattedTemplates);
      } else {
        toast.error('Filter not updated', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Filter not updated ', { position: 'bottom-center' });
    }
  };

  useEffect(() => {
    getAssessmentTemplates();
  }, []);

  useEffect(() => {
    setPageNumber(1);
  }, [templateSearch]);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      getAssessmentTemplates();
    }, 200);

    return () => clearTimeout(debounceSearchTerm);
  }, [templateSearch, pageNumber]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTemplate) return;

    try {
      const response = await deleteAssessmentTemplate(deleteTemplate);

      if (response.ok) {
        await response.json();

        setDeleteTemplate('');
        setIsModalOpen(false);

        await getAssessmentTemplates();

        // Optional success message
        toast.success('Template deleted successfully!', {
          position: 'bottom-center',
        });
      } else {
        toast.error('Failed to delete template', { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting template', { position: 'bottom-center' });
    }
  };

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title='Assessment Templates'
        width='w-full md:w-[800px]'
    >
      <div className='p-0 sm:p-2'>
        <div className='mb-4 sm:mb-6'>
          <input
              type='text'
              placeholder='Search templates...'
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)} // 👈 updates state
              className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200'
          />
        </div>

        <div className='space-y-3 sm:space-y-4'>
          { templates.map((template) => (
            <div
                key={template.templateId}
                className='relative p-3 sm:p-4 bg-white border border-gray-100 rounded-lg hover:shadow-md transition-shadow cursor-pointer'
                onClick={() => onSelectTemplate(template)}
            >
              <button
                  className='flex items-center gap-1 absolute top-2 right-2 px-2.5 py-1 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors text-xs'
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ stop bubbling to parent
                    setDeleteTemplate(template.templateId);
                    setIsModalOpen(true);
                  }}
              >
                <TrashIcon className='w-4 h-4'/>
                Delete
              </button>

              <div className='mb-2 pr-20'>
                <h3 className='text-base sm:text-lg font-medium text-gray-800'>
                  { template.templateTitle }
                </h3>
              </div>
              <p className='text-sm text-gray-600 mb-2'>
                { template.jobTitle }
              </p>
              <div className='flex flex-col gap-3 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between'>
                <div>
                  <span>
                    Created at: { new Date(template.createdAt * 1000).toLocaleDateString('en-GB') }
                  </span>
                  <br/>
                  <span>
                    { template.questionCount } questions
                  </span>
                </div>
                <button
                    className='w-full px-4 py-2 bg-indigo-50 text-indigo-600 rounded-full hover:bg-indigo-100 transition-colors text-xs sm:w-auto sm:py-1'
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectTemplate(template);
                    }}
                >
                  Load Template
                </button>
              </div>
            </div>
          )) }

          { totalPages > 1 && (
            <div className='flex flex-col gap-3 border-t border-gray-200 pt-4 mb-6 sm:flex-row sm:items-center sm:justify-between'>
              <div className='text-sm text-gray-600'>
                Showing { ((pageNumber - 1) * itemsPerPage) + 1 } to{ ' ' }
                { Math.min(pageNumber * itemsPerPage, totalCount) } of { totalCount } results
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                <button
                    onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                    disabled={pageNumber === 1}
                    className='px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>
                { Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                      key={page}
                      onClick={() => setPageNumber(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        pageNumber === page
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    { page }
                  </button>
                )) }
                <button
                    onClick={() => setPageNumber(prev => Math.min(totalPages, prev + 1))}
                    disabled={pageNumber === totalPages}
                    className='px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </div>
            </div>
          ) }

          { /* Move Dialog outside the loop */ }

          <Dialog
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              title='Are you sure you want to delete this template?'
              dialogType={DIALOG_TYPE.ERROR}
              justify='center'
              confirmButtonLable='Delete Template'
              cancelButtonLable='Cancel'
              handleApply={handleDeleteTemplate}
          >
          </Dialog>

        </div>

      </div>
    </Drawer>
  );
};
